import {ChangeLogFlexLayoutTabStore, DEBUG_ASSORTMENTS, ICatalogItemDetailsPathElement} from '@modules'
import {PDFTabMenuItems} from '@modules/pdf'
import {WhiteboardStore} from '@modules/whiteboard'
import {globalStores, IFlexLayoutInlineCatalogItemDetailsConfig, StoresInstance, ui, Workspace} from '@stores'
import {FlexComponentName, VipFlexLayoutTabStore, IFlexLayoutCatalogRootConfig, IBaseFlexLayoutTabConfig, FlexLayoutTabConfig, BuyingBoardFlexLayoutTabStore, IFlexLayoutToolbarWindowConfig, C8FrameFlexLayoutTabStore, FlexLayoutInlineCatalogItemDetailsPaneStore, CatalogRootFlexLayoutStore, FLEX_WEIGHT_DEFAULT, DEBUG_FLEX_LAYOUT} from '@stores/catalog/flexLayout'
import {guid} from '@utils'
import {autorun, isObservableMap, runInAction, trace, when} from 'mobx'
import {deepObserve, IDisposer} from 'mobx-utils'
import {CLASSES, BorderNode, DefaultNodeType, IDraggable, IJsonTabNode, ILayoutMetrics, ITabAttributes, Model, Rect, TabButtonStore, TabSetNode, RowNode, BorderSet} from '../'
import {stringify, action, classNames, computed, jsondiff, observable, toJS, _, utils} from '@api'
import {FlexLayoutNode} from './FlexLayoutNode'
import {AttributeDefinitions} from '../AttributeDefinitions'
import {Attribute} from '../Attribute'

export type MoveTabDirection = 'left' | 'right' | 'down' | 'up'

export interface IFlexTabGroup {
  name: string,
  id: string,
  color: string,
}

export class TabNode<C extends FlexLayoutTabConfig = IBaseFlexLayoutTabConfig> extends FlexLayoutNode<DefaultNodeType & ITabAttributes, IJsonTabNode> implements IDraggable {
  static readonly TYPE = 'tab'
  @observable.ref contentRef: HTMLInputElement
  @observable.ref tabStore: TabButtonStore
  @observable.ref iframe: BuyingBoardFlexLayoutTabStore | C8FrameFlexLayoutTabStore
  @action maximize = () => this.flexLayout.maximizeTab(this.id)
  @action toggleMaximize = () => this.flexLayout.toggleMaximize(this)
  @observable.ref vipTabStore: VipFlexLayoutTabStore
  /**
   * Customizable number to display in tab title
   */
  @observable itemCount?: number

  @computed get includeInPDF() {
    const {component} = this
    return ['catalog', 'catalogItemDetails'].includes(component)
  }

  get componentStore() {
    return this.extraData?.store ?? this.extraData?.whiteboard ?? this._catalogRoot
  }

  /**
   * Can be changed by the tab contents to show a spinner where the caret normally is in the tab title
   */
  @observable isLoading?: boolean = false

  @computed get isInlineDetailsTab() { return this.component === 'inlineCatalogItemDetails'}

  @computed get storesToInject() { return this.vipTabStore?.storesToInject}

  @computed get offscreenDirection(): 'left' | 'right' {
    const {isSelected, tabSet, indexInParent} = this
    return isSelected ? undefined : indexInParent < tabSet?.selected ? 'left' : 'right'
  }

  @computed get tabsOfSameType(): TabNode[] {
    const {model: {_idMap}, component} = this
    return _.values(_idMap).filter(v => v instanceof TabNode && v.component === component && v != this) as TabNode[]
  }

  @action collectAllOfSameType = () => {
    const {tabsOfSameType, tabSet} = this,
      tabsToMove = tabsOfSameType.filter(t => t.tabSet != tabSet)

    tabsToMove.forEach(t => t.moveTo(tabSet))
  }

  @action moveTo = (tabSet: TabSetNode) => {
    const {tabSet: parent} = this
    if (tabSet === parent) return

    this.removeFromParent()

    DEBUG_FLEX_LAYOUT && console.debug(`Move tab '${this.name}' to tabSet '${tabSet.id}'`)
    tabSet.addChild(this)
    this.selectInTabSet()
  }

  @computed get isVisible() {
    const {parent} = this
    if (parent instanceof BorderNode && (!parent.isShowing || !this.selectedInBorder)) return false
    if (parent.hidden) return false

    const r = this.isSelected || this.isEnableRenderOnDemand === false

    if (r && !this.everVisible) { setTimeout(() => this.everVisible = true)}

    return r
  }

  @observable everVisible = false

  /**
   * For operations like duplicate we want to look at our original node we're about to duplicate to use his catalog to display useful info
   */
  @computed get previewNodeId() { return this.attributes.previewNodeId}

  @computed get pageNumber() { return this.flexLayout.tabsInOrder.indexOf(this) + 1}

  @computed get selectedInBorder() {
    const tab = this as TabNode<IFlexLayoutToolbarWindowConfig>
    return tab?.config?.settings?.isToggledOn === true
  }

  @computed get isInTabSet() { return this.parent instanceof TabSetNode}

  @computed get isInBorder() { return this.parent instanceof BorderNode}

  @computed get isMaximized() {
    return this.id === this.flexLayout.maximizedTab?.id
  }

  @computed get displayNameLong() {
    const {displayName, catalogRoot} = this

    const title = catalogRoot?.catalog?.workspaceConfig?.title
    if (!title || displayName === title) return displayName

    return `${title} - ${displayName}`
  }

  @computed get displayName() {
    const {vipTabStore, name, flexLayout, attributes: {config}, component} = this
    const r = config?.nonDefaultName || flexLayout.borderForKnownElement[component] ? name : flexLayout.defaultCatalogNameFor(this)

    // console.log(`displayName`, r, node.id)
    return r ?? vipTabStore?.stores?.strings?.emptyLabel
  }

  @computed get isToolboxTab() { return this.flexLayout.knownTabs[this.id] != null}

  @computed get isEditing() { return this === this.flexLayout.layout?.store?.editingTab}

  @computed get isSelected() {
    return this.tabSet?.selected === this.indexInParent || (this.indexInParent === 0 && (this.tabSet?.selected === -1 || this.tabSet?.selected == null))
  }

  @computed get tabsInGroup(): Array<IJsonTabNode> {
    const {associatedGroup, flexLayout} = this
    if (!associatedGroup) return null

    return flexLayout.tabsByTabGroup[associatedGroup.id] ?? []
  }

  @action setTabGroup = (tabGroup: IJsonTabNode) => {
    this.attributes.tabGroup = tabGroup.id
    this.model._changeListener()
  }

  @computed get canAddTabToGroup() {
    const {isGroupLabel, tabGroup, flexLayout: {tabGroups}} = this
    return !isGroupLabel // && _.size(tabGroups) != 1
  }

  /**
   * A tab group is a custom made subgrouping of tabs with its own name and color that can be independently expanded or collapsed
   * This is the internal index WITHIN said group, starting at 0 and undefined if not in a group at all or -1 if not in the same tabset as the tabgroup
   */
  @computed get indexInTabGroup() {
    const {isGroupLabel, associatedGroup} = this
    if (isGroupLabel || !associatedGroup) return undefined

    const {tabSet} = associatedGroup
    const index = _.findIndex(tabSet.children, (t: FlexLayoutNode) => t.id === this.id, associatedGroup.indexInParent)
    if (index == -1) return index
    return index - associatedGroup.indexInParent - 1
  }

  @computed get associatedGroup(): TabNode {
    const {isGroupLabel, flexLayout: {tabGroups}, attributes, model} = this
    return isGroupLabel ? this : model.nodeById<TabNode>(attributes.tabGroup)
  }

  @computed get groupExpanded() {
    const {associatedGroup} = this

    if (!associatedGroup) return false
    else if (associatedGroup.id === this.id) {
      return this._getAttr('groupExpanded') as boolean
    } else {
      return associatedGroup.groupExpanded
    }
  }

  @computed get isLastItemInTabGroup() {
    const {associatedGroup, indexInTabGroup} = this
    return associatedGroup && _.isNumber(indexInTabGroup) ? indexInTabGroup + 1 === associatedGroup.tabsInGroup.length : false
  }

  @computed get isGroupLabel() {
    return this._getAttr('isGroupLabel') as boolean
  }

  @computed get tabGroup() {
    return this._getAttr('tabGroup') as string | undefined
  }

  @computed get groupColor() {
    return this._getAttr('groupColor') as string | undefined
  }

  /** @hidden @internal */
  static _fromJson(json: IJsonTabNode, model: Model, addToModel: boolean = true) {
    const newLayoutNode = new TabNode(model, json, addToModel)

    if (newLayoutNode.attributes.component === 'catalog' && !newLayoutNode.config) {
      newLayoutNode.attributes.config = {}
    }

    return newLayoutNode
  }

  /** @hidden @internal */
  private static _attributeDefinitions: AttributeDefinitions = TabNode._createAttributeDefinitions()

  /** @hidden @internal */
  private static _createAttributeDefinitions(): AttributeDefinitions {
    const attributeDefinitions = new AttributeDefinitions()
    attributeDefinitions.add('type', TabNode.TYPE, true).setType(Attribute.STRING)
    attributeDefinitions.add('id', undefined).setType(Attribute.STRING)
    attributeDefinitions.add('previewNodeId', undefined).setType(Attribute.STRING)

    attributeDefinitions.add('name', '').setType(Attribute.STRING)
    attributeDefinitions.add('component', undefined).setType(Attribute.STRING)
    attributeDefinitions.add('config', undefined).setType('any')
    attributeDefinitions.add('floating', false).setType(Attribute.BOOLEAN)
    attributeDefinitions.addInherited('tabGroup', 'tabGroup').setType(Attribute.STRING)

    attributeDefinitions.addInherited('groupColor', 'groupColor').setType(Attribute.STRING)
    attributeDefinitions.addInherited('isGroupLabel', 'isGroupLabel').setType(Attribute.BOOLEAN)
    attributeDefinitions.addInherited('groupExpanded', 'groupExpanded').setType(Attribute.BOOLEAN)
    attributeDefinitions.addInherited('enableClose', 'tabEnableClose').setType(Attribute.BOOLEAN)
    attributeDefinitions.addInherited('closeType', 'tabCloseType').setType('ICloseType')
    attributeDefinitions.addInherited('enableDrag', 'tabEnableDrag').setType(Attribute.BOOLEAN)
    attributeDefinitions.addInherited('enableRename', 'enableRename').setType(Attribute.BOOLEAN)
    attributeDefinitions.addInherited('className', 'tabClassName').setType(Attribute.STRING)
    attributeDefinitions.addInherited('icon', 'tabIcon').setType(Attribute.STRING)
    attributeDefinitions.addInherited('enableRenderOnDemand', 'tabEnableRenderOnDemand').setType(Attribute.BOOLEAN)
    attributeDefinitions.addInherited('enableFloat', 'tabEnableFloat').setType(Attribute.BOOLEAN)
    return attributeDefinitions
  }

  /** @hidden @internal */
  @observable private _tabRect?: Rect
  /** @hidden @internal */
  @observable private _extra: Record<string, any> | { store?: FlexLayoutInlineCatalogItemDetailsPaneStore | ChangeLogFlexLayoutTabStore, whiteboard?: WhiteboardStore }
  /** @hidden @internal */
  @observable.ref private _window?: Window

  /** @hidden @internal */
  constructor(model: Model, json: any, addToModel: boolean = true) {
    super(model)

    this._extra = {} // extra data added to node not saved in json

    TabNode._attributeDefinitions.fromJson(json, this._attributes)
    if (addToModel === true) {
      model._addNode(this)
    }

    this._config = json?.config ?? {}
  }

  @observable.ref _catalogRoot: CatalogRootFlexLayoutStore
  @computed get catalogRoot() {
    const {id, flexLayout, flexLayout: {knownTabs}} = this
    if (knownTabs[id]) {
      return flexLayout.activeCatalogRoot
    }

    return this._catalogRoot
  }

  /**
   * Returns the config attribute that can be used to store node specific data that
   * WILL be saved to the json. The config attribute should be changed via the action Actions.updateNodeAttributes rather
   * than directly, for example:
   * this.state.model.doAction(
   *   FlexLayout.Actions.updateNodeAttributes(node.id, {config:myConfigObject}));
   *
   *   // Todo - this needs to be throttled, it's very expensive
   */
  @observable _config: C

  @computed get config(): C {
    return this._config

    trace()
    const {component, componentStore, _attributes: {config}} = this,
      stores: StoresInstance = componentStore?.stores ?? this.storesToInject

    if (component === 'whiteboard') {
      return config
    }

    if (stores && !stores.isGlobalStore) {
      const {
        deployments: {selectedDeploymentId},
        catalog, groupBy, groupBy: {queryParams, groupingSettings: gbgs, nonDefaultSettings: gbs},
        catalog: {settingsStore}, sidebar: {serializableState: sidebar}, filter
      } = stores

      if (catalog) {
        if (component != 'catalog' || (catalog.initialized && groupBy.initialized && filter.hasLoaded)) {
          if (config?.deployment && config.deployment != selectedDeploymentId) return config

          const r: IFlexLayoutCatalogRootConfig & C = _.mergeWith(
            {},
            config,
            {
              settings: {
                //deployment: selectedDeploymentId,
                ...(config?.settings ?? {}),
                workspace: config?.settings?.workspace ?? catalog?.workspace?.id,
                catalog: _.cloneDeep(settingsStore.nonDefaultSettings),
                filter: _.cloneDeep(filter.queryParams),
                groupBy: {
                  queryParams,
                  groupingSettings: Base64.encode(JSON.stringify(gbgs)),
                  byGroup: _.cloneDeep(gbs)
                },
                sidebar
              }
            }, utils.lodashMergeOverwriteArraysFn)

          return r
        }
      }
    }

    return config
  }

  autorun_updateConfigFromSettings = () => autorun(() => {
    const {component, componentStore, _attributes} = this,
      config = toJS(_attributes.config), // Or else we'll not autorun when these are directly changed like opening a border
      stores: StoresInstance = componentStore?.stores ?? this.storesToInject

    //component === 'catalog' && trace()

    if (stores && !stores.isGlobalStore) {
      const {
        deployments: {selectedDeploymentId},
        catalog, groupBy, groupBy: {queryParams, groupingSettings: gbgs, nonDefaultSettings: gbs},
        catalog: {settingsStore}, sidebar: {serializableState: sidebar}, filter
      } = stores
    }

    //this._updateConfig()
    this._updateConfigDebounce()
  }, { name: `Update tab serializable config as underlying catalog changes`})

  override select = () => {
    this.selectInTabSet()
  }

  /**
   * Many components mount and then change their settings, we only want to serialize the whole giant object when they're all done changing things
   */
  _updateConfigDebounce = _.debounce(() => this._updateConfig(), 100)

  _updateConfig = () => {
    const {component, componentStore,  _attributes: {config}} = this,
      stores : StoresInstance = componentStore?.stores ?? this.storesToInject ?? globalStores,
      {reports: {_loadingReport, justLoadedReport}} = stores

    if (_loadingReport || justLoadedReport) return

    if (component === 'whiteboard') {
      this._config = config
    }

    if (stores && !stores.isGlobalStore) {
      const {
        deployments: {selectedDeploymentId},
        catalog, groupBy, groupBy: {queryParams, groupingSettings: gbgs, nonDefaultSettings: gbs},
        catalog: {settingsStore}, sidebar: {serializableState: sidebar}, filter
      } = stores

      if (catalog) {
        if (component != 'catalog' || (catalog.initialized && groupBy.initialized && filter.hasLoaded)) {
          if (config?.deployment && config.deployment != selectedDeploymentId) {
            this._config = config
          }

          const r: IFlexLayoutCatalogRootConfig & C = _.mergeWith(
            {},
            toJS(config),
            {
              settings: {
                //deployment: selectedDeploymentId,
                workspace: config?.settings?.workspace ?? catalog?.workspace?.id,
                catalog: _.cloneDeep(settingsStore.nonDefaultSettings),
                filter: filter.queryParams,
                groupBy: {
                  queryParams,
                  groupingSettings: Base64.encode(JSON.stringify(gbgs)),
                  byGroup: gbs
                },
                sidebar
              }
            }, utils.lodashMergeOverwriteArraysFn)

          this._config = r
        }
      }
    }
    else {
      this._config = config
    }
  }

  get catalogRootConfig() { return this.config as IFlexLayoutCatalogRootConfig}

  @computed get workspaceId() {
    return this?.catalogRootConfig?.settings?.workspace
  }

  @computed get workspace(): Workspace {
    const {workspaceId} = this
    return workspaceId ? globalStores.workspaces.workspaceFor(workspaceId) : _.first(globalStores.workspaces.workspaces)
  }

  registerEffects = () => {
    return [
      this.autorun_updateConfigFromSettings(),
      autorun(() => {
        if (this.component !== 'catalog') return

        const {workspace, id, flexLayout: {}} = this

        if (!workspace?.visible) { // This workspace is invalid or you shouldn't be able to see it.
          this.removeFromParent(true)
          return
        }
        //if (deployment && deployment != deployments.selectedDeploymentId) return null

        runInAction(() => {
          let stores: StoresInstance

          stores = new StoresInstance(`Stores for tab ${id}`, workspace, `${id ?? this.name}`)
          // if (config?.settings?.preset) {
          //   const {preset} = config.settings, {catalog: {presets: {loadPresetForCurrentWorkspace, presetsById}}} = stores
          //   when(() => stores.catalog.hasFinishedFirstLoad, () => {
          //     DEBUG && console.debug(`Loading preset for catalog - ${id} - ${stores.catalog.title} - preset '${preset}'`, {stores})
          //     config.settings.preset = null
          //     loadPresetForCurrentWorkspace(presetsById[preset])
          //   })
          // }

          // Do this before calling deserialize as they look to see if they're inside a catalog root
          const {catalogRootConfig} = this,
            sidebarConfig = catalogRootConfig?.settings?.sidebar,
            filterConfig = catalogRootConfig?.settings?.filter

          when(() => stores.catalog.hasLoadedConfig && stores.catalog.hasInitializedSeasonStores, () => {
            stores.sidebar.deserializeFromSettings(sidebarConfig)
            stores.filter.deserializeFromQueryParams(filterConfig)
          })

          /**
           * YFTS-9585 - Persist filters when workspace is changed on a tab.  This is made difficult as the filter state is mutated from various sources like season selectors and the like, thus we create a bunch of whens() that watch for the
           * filter state to be created and then replace it with the prior workspace's version of the data
           */
          if (this._catalogRoot?.storesToInject?.filter) {
            const {_catalogRoot: {storesToInject: {filter}}} = this
            const existingFilters = (filter?.filtersWithSelections ?? []).map(({group, state: {selected, excluded}}) => ({field: group, selected: toJS(selected), excluded: toJS(excluded)}))
            if (!_.isEmpty(existingFilters)) {
              when(() => stores.catalog.hasLoadedConfig && stores.filter.initialized, () => {
                const {catalog, filter} = stores
                existingFilters.forEach(({field, selected, excluded}) => {
                  if (catalog._fieldByName[field]) {
                    when(() => {
                      const group = filter.groupFor(field)
                      return !_.isEmpty(group)
                    }, () => {
                      const group = filter.groupFor(field)
                      isObservableMap(group.selected) ? group.selected.replace(selected) : (group.selected = selected)
                      isObservableMap(group.excluded) ? group.excluded.replace(excluded) : (group.excluded = excluded)
                    })
                  }
                })
              })

            }
          }

          /**
           * YFTS-9701 persist group by when switching between workspaces/assortments
           */
          if (this._catalogRoot?.storesToInject?.groupBy?.isGroupedView) {
            const {_catalogRoot: {storesToInject: {groupBy: {hierarchy}}}} = this,
              existing = hierarchy.slice()

            !_.isEmpty(existing) && when(() => stores.catalog.hasLoadedConfig && stores.groupBy.initialized, () => {
              stores.groupBy.hierarchy = existing.filter(f => stores.groupBy.groupingByName.has(f))
            })
          }

          this._extra = {}
          this.vipTabStore && (this.vipTabStore.tab._extra = {})
          this._catalogRoot?.dispose()
          this._catalogRoot = new CatalogRootFlexLayoutStore(stores, {flexNode: this})
          // this.vipTabStore.storesToInject
          //this._catalogRoot.hasFinishedFirstLoad = true
        })
      }, {name: `Setup stores and deserialize settings for tab ${this.id} - ${this.displayName}`})
    ].filter(_.identity)
  }

  @action onCopyExtraStoreToWindow = () => {
    window['flexExtraStore'] = this
    DEBUG_ASSORTMENTS && console.debug(`Copied store for '${this.displayName}' into window.flexExtraStore and your clipboard`)
    navigator.clipboard.writeText(`window.flexExtraStore`)
  }

  get window() {
    return this._window
  }

  @computed get tabRect() {
    return this._tabRect ?? Rect.empty()
  }

  /** @hidden @internal */
  _setTabRect = (rect: Rect) => {
    if (!this._tabRect?.equals(rect)) {
      this._tabRect = rect
    }
  }

  set name(name) {
    this._setName(name)
  }

  @computed get name() {
    let r = this._getAttr('name') as string
    if (!_.isString(r)) {
      r = ''
    }
    if (this.component === 'catalog') {
      if (!(this.config as IFlexLayoutCatalogRootConfig)?.nonDefaultName) {
        return ''
      }
    } else if (!r) {
      const {vipTabStore} = this
      if (vipTabStore) {
        const {storesToInject: {strings}} = vipTabStore

        switch (this.component) {
          case 'catalogAggregateDetails':
            return strings.aggregateDetails

          case 'filters':
            return strings.filters

          case 'flexLayoutSettings':
            return 'Layout Settings'

          case 'libraries':
            return strings.libraries

          case 'pdfOptions':
            return 'PDF'

          case 'vipBuying':
            return 'Buying Board'

          default:
            return strings.emptyLabel
        }
      }
    }

    return r
  }

  @computed get component() {
    return this._getAttr('component') as string | FlexComponentName | undefined
  }

  /**
   * Returns an object that can be used to store transient node specific data that will
   * NOT be saved in the json.
   */
  get extraData() {
    return this._extra
  }

  get isFloating() {
    return this._getAttr('floating') as boolean
  }

  get icon() {
    return this._getAttr('icon') as string | undefined
  }

  get isEnableClose() {
    return this._getAttr('enableClose') as boolean
  }

  get closeType() {
    return this._getAttr('closeType') as number
  }

  get isEnableFloat() {
    return this._getAttr('enableFloat') as boolean
  }

  get isEnableDrag() {
    return this._getAttr('enableDrag') as boolean
  }

  get isEnableRename() {
    return this._getAttr('enableRename') as boolean
  }

  get className() {
    return this._getAttr('className') as string | undefined
  }

  get isEnableRenderOnDemand() {
    return this._getAttr('enableRenderOnDemand') as boolean
  }

  /** @hidden @internal */
  _setName(name: string) {
    if (!_.isString(name)) throw new Error(`${name} must be a string`)

    this._attributes.name = name
    Object.assign(this.attributes.config, {nonDefaultName: name != ''})

    if (this._window && this._window.document) {
      this._window.document.title = name
    }
  }

  /** @hidden @internal */
  _setFloating(float: boolean) {
    this._attributes.floating = float
  }

  /** @hidden @internal */
  @action _layout = (rect: Rect, metrics: ILayoutMetrics) => {
    if (!rect.equals(this.rect)) {
      this._rect = rect
      this._fireEvent('resize', {rect})
    }
  }

  @computed get associatedToolboxBorderTab(): TabNode<IFlexLayoutToolbarWindowConfig> {
    const {model: {_idMap}, isToolboxTab, id} = this
    if (!isToolboxTab) return null

    return _idMap[id.substring(0, id.length - this.flexLayout.borderTabAsRealTabIdSuffix.length)] as TabNode<IFlexLayoutToolbarWindowConfig>
  }

  /** @hidden @internal */
  @action _delete = () => {
    const {parent, associatedToolboxBorderTab, isToolboxTab, indexInParent} = this,
      parentNode = (parent as TabSetNode | BorderNode)

    parentNode._setSelected(indexInParent - 1)
    parentNode._remove(this)
    this._fireEvent('close', {})

    if (associatedToolboxBorderTab) {
      _.merge(associatedToolboxBorderTab.config, {settings: {isToggledOn: false}})
    }

    if (_.isEmpty(parent?.children) && (parent.id.endsWith('Sidebar') || isToolboxTab)) {
      (parent as TabSetNode).removeFromParent(true)
      const borderTab = this.id.substring(0, this.id.length - 3) // Remove the word 'Tab'
      const borderNode = this.model.nodeById(borderTab)
      if (borderNode) {
        _.merge(borderNode.attributes, {config: {settings: {isToggledOn: false}}})
      }
    }
  }

  @observable.ref ref: HTMLDivElement

  /** @hidden @internal */
  @computed get asJson() {
    const json = {}
    TabNode._attributeDefinitions.toJson(json, this._attributes)
    return json
  }

  /** @hidden @internal */
  _updateAttrs(json: any) {
    TabNode._attributeDefinitions.update(json, this._attributes)
  }

  /** @hidden @internal */
  _getAttributeDefinitions() {
    return TabNode._attributeDefinitions
  }

  /** @hidden @internal */
  _setWindow(window: Window | undefined) {
    this._window = window
  }

  /** @hidden @internal */
  static getAttributeDefinitions() {
    return TabNode._attributeDefinitions
  }

  get border(): BorderNode {
    return this.parent instanceof BorderNode ? this.parent as BorderNode : null
  }

  @computed get tabSet(): TabSetNode { return this.parent instanceof TabSetNode ? this.parent as TabSetNode : null }

  @computed get lastContiguousGroupTabIndex() {
    const {tabSet, isGroupLabel, id} = this
    return isGroupLabel ? utils.findLastIndexLtoR(tabSet.children, (item: TabNode) => item.tabGroup === id, this.indexInParent + 1) : undefined
  }

  @computed get selectedIndexInGroup() {
    const {model, associatedGroup, groupExpanded} = this
    if (groupExpanded && associatedGroup) {
      const {id: tabGroupId, tabSet, tabSet: {selectedNode}} = associatedGroup,
        selectedIndex = !selectedNode ? -1 : tabSet.children.filter((t: TabNode) => t.tabGroup == tabGroupId).findIndex(t => t.id === selectedNode.id)

      return selectedIndex
    }

    return undefined
  }

  @computed get tabButtonClassName() {
    const {tabSet, isSelected} = this

    let baseClassName = CLASSES.FLEXLAYOUT__TAB_BUTTON
    const r = classNames(
      baseClassName,
      baseClassName + '_' + tabSet?.tabLocation,
      isSelected ? baseClassName + '--selected'
                 : baseClassName + '--unselected',
      this.className)

    return r
  }

  @action selectInTabSet = () => {
    DEBUG_FLEX_LAYOUT && !TEST && console.debug(`Select tab ${this.displayName}`)
    const {flexLayout} = this
    flexLayout.setActiveTab(this)
  }

  @computed get nextSettings() {
    const {catalogRoot} = this,
      nextSettings = catalogRoot?.nextSettings ?? this.config as IFlexLayoutCatalogRootConfig

    return nextSettings
  }

  @observable ignoreSettingUpdates = false
  throttleUpdateFlexNodeCustomSettings = _.throttle(action(() => {
    if (this.ignoreSettingUpdates) return

    const {nextSettings, vipTabStore} = this

    if (!vipTabStore) return

    const {stores: {authStore}} = vipTabStore
    //    diff = jsondiff.diff(this?.attributes?.config, nextSettings)
    // console.debug(`[flexLayout] - [${tab.attributes?.name} _debounceUpdateFlexNodeCustomSettings`, {diff, newSettings, cs: settingsStore.settings, gbs})

    //if (diff) {
    if (!authStore.userIsAuthenticated) return

    DEBUG_FLEX_LAYOUT && console.debug(`[TabNode.autorun_updateFlexNodeCustomSettings] - Serializing '${this?.displayName}' - ${this.id} due to settings change`, {nextSettings})

    if (this.component === 'catalog') {
      const {attributes: {config}} = this
      _.assign(config, _.omit(nextSettings, 'settings'));
      (config as any).settings = nextSettings.settings
    }

    this.model.onChange()

    // Why change the json?  It's generated...
    //   const {json} = this
    //   if (json) { // The node might have been removed from the model
    //     _.assign(json.config, _.omit(nextSettings, 'settings'))
    //     json.config.settings = nextSettings.settings // If we do a strict merge above we won't remove fields when they go from non-default to default (viewMode = 'pdf' → 'card')
    //   }
    //

    // _.assign(tab.config, _.omit(nextSettings, 'settings'))
    // _.merge(tab, {settings: nextSettings.settings}) // If we do a strict merge above we won't remove fields when they go from non-default to default (viewMode = 'pdf' → 'card')
    // } else {
    //   // console.debug(`Ignoring settings change as no diff detected`)
    // }
  }), ui.settings.reports.updateSettingsDebounce)

  // _watchSettings: IDisposer

  autorun_updateFlexNodeCustomSettings = () => {
    return autorun(() => {
      if (this.ignoreSettingUpdates) return

      const next = toJS(this.nextSettings); // todo - change to deepObserver
      this.throttleUpdateFlexNodeCustomSettings()

      // this._watchSettings?.()
      // this._watchSettings = deepObserve(nextSettings, () => {
      //   console.log(`Settings changed in tab '${this.id}`)
      //   this.throttleUpdateFlexNodeCustomSettings()
      // })
    }, {name: `${this.flexLayout.stores.storeId} - Update flex layout catalog tab catalog/groupBy settings on change`, delay: 20})
  }

  @computed get availableMoveDirections(): Set<MoveTabDirection> {
    const r = new Set<MoveTabDirection>()
    if (this.isInTabSet) { // Can be in a border, no move available
      const {tabSet, tabSet: {row, isInColumn}} = this

      if (tabSet.children.length > 1) {
        // We can always split a tabset with multiple tabs
        (['left', 'right', 'up', 'down'] as MoveTabDirection[]).forEach(d => r.add(d))
      } else {
        if (isInColumn) {
          r.add('left')
          r.add('right')
        }
        if (row.isHorizontal) {
          r.add('up')
          r.add('down')
        }

        if (tabSet.indexInParent > 0) {
          (['left', 'up'] as MoveTabDirection[]).forEach(d => r.add(d))
        }

        if (tabSet.indexInParent < row.children.length - 1) {
          (['right', 'down'] as MoveTabDirection[]).forEach(d => r.add(d))
        }
      }
    }

    return r
  }

  @action duplicate = (direction: MoveTabDirection | 'inline') => {
    const {tabSet, displayName, tabSet: {row: parentRow}} = this,
      newTabJson: Partial<IJsonTabNode> = {
        ..._.cloneDeep(_.omit(this.asJson, 'id)')),
        name: displayName.startsWith('Duplicate of ') ? `${displayName} (1)` : `Duplicate of ${displayName}`,
        id: guid()
      }

    const newTab = tabSet.addChildJson(newTabJson, direction === 'inline', direction === 'inline' ? this.indexInParent + 1 : tabSet.children.length)
    if (direction != 'inline') {
      newTab.move(direction)
    }
  }

  @action move = (direction: MoveTabDirection) => {
    const tab = this.model.nodeById<TabNode>(this.id)
    const {tabSet, displayName, id, isPreviewLayout, model, indexInParent} = tab
    DEBUG_FLEX_LAYOUT && DEBUG && console.debug(`moveTab(${displayName})`, {id, direction, isPreview: isPreviewLayout, before: model.shorthand})

    if (!tab.availableMoveDirections.has(direction)) {
      return
    }

    if (isPreviewLayout) {
      tab.attributes.className = 'preview'
    }

    /*
      Detect noops and return

      A | B - MoveLeft A, MoveRight B
      A / B - MoveUp A

     */
    if ((direction === 'up' || direction === 'down') && tabSet.row.isHorizontal) {
      // We are not in a column layout, another 'row' will need to be inserted

      // move(B, 'up') AB -> B / A          move(A, 'up)  S | AB -> S | A /B
      // move(A, 'up') AB -> A / B

      const parentRow = tabSet.row

      // Pull out the tab and put it into its own row object

      // Remove the tab we're moving from their existing tabset
      tab.removeFromParent(false)

      // Remove the tabset our original tab belonged to from their row
      // When there's a sidebar open we may be going from Ab | S -> move(b, 'down') ->
      //    tab = b, initialTabSet = [A, S]

      // let leftSidebar: RowNode | TabSetNode,
      //   rightSidebar: RowNode | TabSetNode
      //
      // const firstItemInOriginalTabSet = _.first<RowNode | TabSetNode>(tabSet.children)
      // if (firstItemInOriginalTabSet.isLayoutSidebar) {
      //   leftSidebar = firstItemInOriginalTabSet
      //   leftSidebar.removeFromParent()
      // }
      //
      // const lastItemInOriginalTabSet = _.last<RowNode | TabSetNode>(tabSet.children)
      // if (lastItemInOriginalTabSet.isLayoutSidebar) {
      //   rightSidebar = lastItemInOriginalTabSet
      //   rightSidebar.removeFromParent()
      // }

      const initialTabSet = tabSet,
        initialTabSetIndex = tabSet.indexInParent,
        initialTabSetWeight = initialTabSet.weight

      initialTabSet.removeFromParent(false)

      // Create the new column (row in a row)
      const column = parentRow.addChildJson(
        {
          type: 'row',
          weight: initialTabSetWeight
        },
        initialTabSetIndex) as RowNode

      // Move our tab into a new tabset and put it in the column
      const newTabSet = column.addChildJson({type: 'tabset', weight: FLEX_WEIGHT_DEFAULT, children: []}) as TabSetNode
      newTabSet.addChild(tab)
      tab.selectInTabSet()

      // Take the other tabs from the tabset we were in and move it to the other column
      column.addChild(initialTabSet, direction === 'up' ? 1 : 0)

      column.children.forEach(c => c.attributes.weight = FLEX_WEIGHT_DEFAULT)

      // leftSidebar && parentRow.addChild(leftSidebar, 0)
      // rightSidebar && parentRow.addChild(rightSidebar)
    } else { // We don't need to create new RowNodes, however we may need to create new tabsets or move between existing ones
      // We're going to get deleted after move
      if (tabSet.children.length === 1 && tabSet.isInColumn && (direction === 'left' || direction === 'right')) {
        let {row, row: {indexInParent}} = tabSet,
          grandParentIndex = row?.row?.indexInParent

        // A / B -> a.move(left) -> A | B
        // a in a column in a row
        // What about A / B / C - b.move('left') -> B | (A / C)

        tab.removeFromParent(true)
        model.tidy()

        while (!row.isInModel) {
          row = row.row
        }

        let sibling = row.children[direction === 'left' ? Math.max(0, grandParentIndex - 1) : grandParentIndex]

        sibling && sibling._setWeight(tabSet.weight)
        const newTabSet = row.addChildJson({
          type: 'tabset',
          children: [],
          weight: tabSet.weight
        }, direction === 'left' ? indexInParent : indexInParent + 1)

        newTabSet.addChild(tab)
        tab.selectInTabSet()
        return
      }

      let row: RowNode = tabSet.row, indexInRowNode = tabSet.indexInParent,
        weight = tabSet.weight

      if ((direction === 'left' || direction === 'right') && !row.isHorizontal) {
        indexInRowNode = row.indexInParent
        weight = row.weight
        row = row.parent as RowNode
      } else if (direction === 'up' || direction === 'down' && row.isHorizontal) {
        // Are we already in a column?
        if (row.parent && row.parent.isVertical) {
          indexInRowNode = row.indexInParent
          weight = row.weight
          row = row.parent as RowNode
        }
      }

      let indexToSet = direction === 'left' || direction === 'up' ? indexInRowNode - 1 : indexInRowNode + 1

      // Insert a new tabset if needed
      if (indexToSet === -1 || (indexToSet >= row.children.length) || (indexToSet < row.children.length && row.children[indexToSet]?.isLayoutSidebar)) {
        if (tabSet.children.length <= 1) return tab
        // Insert a new tabset to our left
        if (indexToSet === -1) { indexToSet = 0}

        if (row.children[indexToSet]?.isLayoutSidebar) {
          indexToSet = direction == 'left' || direction === 'up' ? indexToSet + 1 : indexToSet - 1
        }

        row.addChildJson({
          type: 'tabset',
          weight,
          children: []
        }, indexToSet)
      }

      let toAddTo = row._children[indexToSet]
      if (toAddTo instanceof RowNode) {
        toAddTo = _.first(toAddTo.children)
      }

      tab.removeFromParent(true)
      toAddTo.addChildren([tab])
      tab.selectInTabSet()
      tab.model.tidy()
    }
  }

  /**
   * When the user double-clicks a detail item we want both the catalog they're in and the opened details to be visible after the operation.
   *
   * * To achieve this we do the following:
   *
   * Walk up to the parent tabset's row and see if we're in a horizontal row.
   *    If so, grab our right neighbor
   *      Is it a vertical column?
   *          ???
   *      Is it a tabset containing at least one details pane from our workspace?
   *          If so, add the new detail tab to that pane and select it [ DONE ]
   *      Else: We have a neighbor in a horizontal row to our right, but it's not suitable for our new tab
   *        Create a new tabset in the row to our right, add the new tab to it, and select it  [ DONE ]
   *        Else: No right neighbor
   *        Create a horizontal row if needed, move our existing tabset to it, create a tabset to our right, add the new tab, and finally select it [ DONE ]
   * @param catalogItemId
   * @param name
   */
  @action openCatalogItemDetailsInNewTab = ({path, workspace, selectedPathIndex = 0, name, parentTab}: { path: Array<ICatalogItemDetailsPathElement>, selectedPathIndex?: number, workspace?: string, name?: string, parentTab?: string }) => {
    DEBUG_FLEX_LAYOUT && console.debug(`openCatalogItemDetailsInNewTab()`, {name, workspace, path, parentTab, tab: this})

    const {flexLayout: {isMaximized, activeTab}} = this
    if (isMaximized && activeTab?.component === 'catalog') {
      DEBUG_FLEX_LAYOUT && console.debug(`Opening in sidebar (we're maximized)`, {name, workspace, path})

      // if (imageId && sidebar.right.selectedPathItem?.imageId == imageId && sidebar.right.open) {
      //   sidebar.right.onClose()
      // } else {
      const {storesToInject: {sidebar}} = this
      if (sidebar.right.open && stringify(sidebar.right.path) === stringify(path)) {
        sidebar.right.open = false
      } else {
        sidebar.right.path = path.slice(0, 1)
        sidebar.right.selectedPathIndex = selectedPathIndex
        sidebar.right.open = true
      }

      return
      // }
      // else if (library) {
      //     sidebar.right.navigateToLibrary({...onClickImageProps, libraryId: library.id, where: [{value: id}], imageId, imageWorkspace}, true)
      //   } else {
      //     sidebar.right.navigateToLibrary({...onClickImageProps, where: [{value: this.id}], imageId, imageWorkspace}, true)
      // }
    } else {
      const {
        tabSet, config, flexLayout: {moveTab},
        tabSet: {neighborRight, neighborDown}
      } = this

      let existingTabSet: TabSetNode | RowNode = neighborRight?.isLayoutSidebar ? neighborDown : neighborRight
      if (existingTabSet) {
        while (existingTabSet instanceof RowNode) {
          existingTabSet = _.find(existingTabSet.children, c => !c.isLayoutSidebar) as TabSetNode | RowNode
        }

        if (!existingTabSet) {} else if (existingTabSet instanceof TabSetNode) {
          const existingTab = existingTabSet.tabs.find(t => _.isEqual(t.attributes?.config?.settings?.detailsPath, path))
          if (existingTab) {
            existingTab.selectInTabSet()
            const c = existingTab.config as IFlexLayoutInlineCatalogItemDetailsConfig
            c.settings.selectedPathIndex = selectedPathIndex

            existingTabSet.uncollapse()
            return
          }
        } else {
          debugger
        }
      }

      const {flexLayout: {create: {createItemDetailsTab}}, storesToInject: {site: {documentAspectRatio}}} = this
      const newTab = createItemDetailsTab({
        tabSet,
        name,
        indexInParent: this.indexInParent + 1,
        workspace: workspace ?? (config as IFlexLayoutCatalogRootConfig)?.settings?.workspace,
        path,
        parentTab
      })

      moveTab(newTab, neighborRight?.isLayoutSidebar || documentAspectRatio < 1.33 ? 'down' : 'right')

      newTab.tabSet.uncollapse()

      //
      // if (neighborRight?.isLayoutSidebar) {
      //   // Create a vertical split
      //
      //   const {neighborDown} = tabSet
      //   if (neighborDown instanceof TabSetNode /*&& isRightNeighborShowingDetailPanes*/) {
      //     // Does this node already exist?
      //     const existingNode = neighborDown.children.find((t: TabNode<IFlexLayoutInlineCatalogItemDetailsConfig>) => _.isEqual(t?.config?.settings?.detailsPath, newTabJson.config.settings?.detailsPath)) as TabNode
      //     if (existingNode) {
      //       console.debug(`Select existing node in tabset - ${name}`)
      //       existingNode.selectInTabSet()
      //     } else {
      //       // Add the new tab to this tabset and select it
      //       console.debug(`Adding new tab to ${neighborRight} existing node in tabset - ${name}`)
      //       neighborDown.addChildJson(newTabJson, true)
      //     }
      //   } else {
      //     const newTabSet: IJsonTabSetNode = {
      //       type: 'tabset',
      //       children: [
      //         newTabJson
      //       ],
      //       weight: FLEX_WEIGHT_DEFAULT
      //     }
      //
      //     // Create a row below us
      //     const {parent} = tabSet
      //
      //     if (parent instanceof RowNode) {
      //       if (!parent.isHorizontal) {
      //         parent.children.forEach(t => t.isLayoutSidebar ? FLEX_WEIGHT_TOOLBOX_SIDEBAR : FLEX_WEIGHT_DEFAULT)
      //         parent.addChildJson(newTabSet)
      //       } else if (parent.isHorizontal) {
      //         const newColumnJson: IJsonRowNode = {
      //           weight: 1,
      //           type: 'row',
      //           children: _.cloneDeep([tabSet.json, newTabSet].map(t => ({...t, weight: t.isLayoutSidebar ? FLEX_WEIGHT_TOOLBOX_SIDEBAR : FLEX_WEIGHT_DEFAULT})))
      //         }
      //         parent.addChildJson(newColumnJson, tabSet.indexInParent)
      //         removeNodeFromParent(tabSet)
      //       }
      //       //  }
      //     }
      //   }
      // }
      // else if (neighborRight instanceof RowNode) {
      //   if (!neighborRight.isHorizontal) {
      //     // Find which row has detail panes
      //     const existingNode = _.flatMap(neighborRight.children.map(c => c.children)).find((t: TabNode<IFlexLayoutInlineCatalogItemDetailsConfig>) => _.isEqual(t?.config?.settings?.detailsPath, newTabJson.config.settings?.detailsPath)) as TabNode
      //     if (existingNode) {
      //       console.debug(`Select existing node in tabset - ${name}`)
      //       existingNode.selectInTabSet()
      //     } else {
      //       let tabSetToAddTo = neighborRight.children.find((ts: TabSetNode) => ts instanceof TabSetNode) ?? _.first(neighborRight.children)  // ts.children.some((t: TabNode) => t.component === 'inlineCatalogItemDetails')
      //
      //       while (tabSetToAddTo instanceof RowNode) {
      //         tabSetToAddTo = tabSetToAddTo.children[0]
      //       }
      //       (tabSetToAddTo as TabSetNode).addChildJson(newTabJson, true)
      //     }
      //   }
      // }
      // // Is our right neighbor a tabset containing at least one details pane from our workspace?
      // else if (neighborRight instanceof TabSetNode /*&& isRightNeighborShowingDetailPanes*/) {
      //   // Does this node already exist?
      //   const existingNode = neighborRight.children.find((t: TabNode<IFlexLayoutInlineCatalogItemDetailsConfig>) => _.isEqual(t?.config?.settings?.detailsPath, newTabJson.config.settings?.detailsPath)) as TabNode
      //   if (existingNode) {
      //     console.debug(`Select existing node in tabset - ${name}`)
      //     existingNode.selectInTabSet()
      //   }
      //   else {
      //     // Add the new tab to this tabset and select it
      //     console.debug(`Adding new tab to ${neighborRight} existing node in tabset - ${name}`)
      //     neighborRight.addChildJson(newTabJson, true)
      //   }
      // }
      // else {
      //   const newTabSet: IJsonTabSetNode = {
      //     type: 'tabset',
      //     children: [
      //       newTabJson
      //     ],
      //     weight: FLEX_WEIGHT_DEFAULT
      //   }
      //
      //   //if (!rowNeighborRight) {
      //     // Create a column to our right
      //     const {parent} = tabSet
      //
      //     if (parent instanceof RowNode) {
      //
      //       if (parent.isHorizontal) {
      //         parent.children.forEach(t => t._setWeight(t.isLayoutSidebar ? FLEX_WEIGHT_TOOLBOX_SIDEBAR : FLEX_WEIGHT_DEFAULT))
      //         parent.addChildJson(newTabSet)
      //       }
      //       else if (!parent.isHorizontal) {
      //         const newRowNode : IJsonRowNode = {
      //           weight:1,
      //           type: 'row',
      //           children: _.cloneDeep([tabSet.json, newTabSet].map(t => ({...t, weight: t.isLayoutSidebar ? FLEX_WEIGHT_TOOLBOX_SIDEBAR : FLEX_WEIGHT_DEFAULT})))
      //         }
      //         parent.addChildJson(newRowNode)
      //         removeNodeFromParent(tabSet)
      //       }
      //   //  }
      //   }
      // }

      // const useJson = true
      // if (useJson) { // slow
      //   const {json} = tabSet
      //
      //   json.children.splice(tab.indexInParent + 1, 0, newTabJson)
      // }
      // else {
      //   const pos = tabSet._addChild(TabNode._fromJson(newTabJson, model), tab.indexInParent + 1)
      //   //tabSet.attributes.selected = pos
      //   model._changeListener()
      // }
    }
  }

  @computed get shorthand() {
    const {id, isSelected} = this
    let r = id.length === 1 ? _.toLower(id) : `t`
    return isSelected ? _.toUpper(r) : r
  }

  @action tabHeader_onMouseEnter = () => {
    const {model} = this
    model.hoverTab = this
  }

  @action tabHeader_onMouseLeave = () => {
    const {model} = this
    if (model.hoverTab == this) {
      model.hoverTab = null
    }
  }

  @computed get tabMenuItems() {
    switch (this.component) {
      case 'pdf':
        return <PDFTabMenuItems/>
      default:
        return null
    }
  }
}



// WEBPACK FOOTER //
// ./src/stores/catalog/flexLayout/flexlayout-react/src/model/TabNode.tsx